import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import axios from "axios";
import { SiteImage, PLACEHOLDER_COLOR } from "@/components/SiteImage";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServicesPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Scroll to anchor on page load or hash change
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure page is rendered
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const navbarHeight = 80; // Account for fixed navbar
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition - navbarHeight,
            behavior: "smooth"
          });
        }
      }, 100);
    }
  }, [location.hash]);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const res = await axios.get(`${API}/galleries/ydelser`);
        // Sort images by order and extract URLs
        const sortedImages = (res.data?.images || [])
          .sort((a, b) => a.order - b.order)
          .map(img => img.url);
        setImages(sortedImages);
      } catch (err) {
        console.error("Error fetching service images:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, []);

  // Get image for a specific service index (0-3), with fallback
  const getServiceImage = (index) => {
    return images[index] || null;
  };

  // Render image using SiteImage component
  const renderServiceImage = (index, alt) => {
    const imageUrl = getServiceImage(index);
    if (loading) {
      return (
        <div 
          className="w-full h-full animate-pulse" 
          style={{ backgroundColor: PLACEHOLDER_COLOR }}
        />
      );
    }
    if (!imageUrl) {
      return (
        <div 
          className="w-full h-full flex items-center justify-center"
          style={{ backgroundColor: PLACEHOLDER_COLOR }}
        >
          <span className="text-white/50 text-sm">Billede kommer snart</span>
        </div>
      );
    }
    return (
      <SiteImage
        src={imageUrl}
        alt={alt}
        className="w-full h-full"
        containerClassName="w-full h-full"
      />
    );
  };

  return (
    <div data-testid="services-page" className="pt-20">
      <SEO 
        title="Ydelser | Birk Anlæg"
        description="Se vores komplette udvalg af anlægsydelser: Flisebelægning, natursten, rullegræs, hegn, træfældning, rodfræsning og anlægsopgaver. Professionelt udført med fokus på kvalitet og holdbarhed."
        canonical="/ydelser"
      />
      {/* Hero */}
      <section className="bg-[#1A3C34] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            data-testid="services-title"
            className="text-4xl md:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Vores ydelser
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Vi udfører anlægsarbejde med fokus på kvalitet og holdbarhed. 
            Fra belægning til grønne opgaver leverer vi et gennemarbejdet resultat.
          </p>
        </div>
      </section>

      {/* Service 1 - Flisebelægning */}
      <section id="flisebelaegning" data-testid="service-section-flisebelaegning" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="flex flex-col">
              {/* Heading - always first */}
              <div>
                <span className="text-[#1A3C34] font-medium text-sm uppercase tracking-wider">
                  Terrasser, gangarealer og indkørsler
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-[#1A3C34] mt-2 mb-6" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Flisebelægning
                </h2>
              </div>
              
              {/* Image - mobile only, appears after heading */}
              <div className="lg:hidden aspect-[4/3] overflow-hidden rounded-lg mb-8">
                {renderServiceImage(0, "Flisebelægning")}
              </div>
              
              {/* Description, list, CTA */}
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Vi udfører flisebelægning til terrasser, gangarealer og indkørsler med fokus på en stabil opbygning og en præcis udførelse.
                Arbejdet planlægges ud fra omgivelser, belastning og brug, så belægningen fungerer i praksis og fremstår gennemført.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Gennemarbejdet bundopbygning tilpasset belastning</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Præcise linjer, overgange og afslutninger</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Omhyggelig tilpasning omkring bygninger og eksisterende forhold</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Vejledning i materialer og mønstre med blik for helhed og udtryk</span>
                </li>
              </ul>
              <Link to="/kontakt" data-testid="service-cta-flisebelaegning" data-cta="primary" data-cta-variant="green" className="inline-flex items-center gap-2 bg-[#1A3C34] text-white rounded-full px-8 py-4 font-medium">
                Kontakt os <ArrowRight size={18} />
              </Link>
            </div>
            {/* Image - desktop only */}
            <div className="hidden lg:block aspect-[4/3] overflow-hidden rounded-lg">
              {renderServiceImage(0, "Flisebelægning")}
            </div>
          </div>
        </div>
      </section>

      {/* Service 2 - Natursten */}
      <section id="natursten" data-testid="service-section-natursten" className="py-20 md:py-28 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="lg:order-2 flex flex-col">
              {/* Heading - always first */}
              <div>
                <span className="text-[#1A3C34] font-medium text-sm uppercase tracking-wider">
                  Belægning og konstruktion i natursten
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-[#1A3C34] mt-2 mb-6" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Natursten
                </h2>
              </div>
              
              {/* Image - mobile only, appears after heading */}
              <div className="lg:hidden aspect-[4/3] overflow-hidden rounded-lg mb-8">
                {renderServiceImage(1, "Natursten")}
              </div>
              
              {/* Description, list, CTA */}
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Vi udfører arbejde med natursten, hvor materialer, opbygning og udførelse stiller særlige krav.
                Opgaverne omfatter både belægninger og konstruktioner og udføres med respekt for materialernes karakter og det ønskede udtryk.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Belægninger i brosten og chaussésten</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Opbygning af kampestensmurer og afgrænsninger</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Specialopgaver med fokus på mønstre, linjer og udtryk</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Tilpasning til eksisterende belægninger og anlæg</span>
                </li>
              </ul>
              <Link to="/kontakt" data-testid="service-cta-natursten" data-cta="primary" data-cta-variant="green" className="inline-flex items-center gap-2 bg-[#1A3C34] text-white rounded-full px-8 py-4 font-medium">
                Kontakt os <ArrowRight size={18} />
              </Link>
            </div>
            {/* Image - desktop only */}
            <div className="lg:order-1 hidden lg:block aspect-[4/3] overflow-hidden rounded-lg">
              {renderServiceImage(1, "Natursten")}
            </div>
          </div>
        </div>
      </section>

      {/* Service 3 - Rullegræs */}
      <section id="rullegraes" data-testid="service-section-rullegraes" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="flex flex-col">
              {/* Heading - always first */}
              <div>
                <span className="text-[#1A3C34] font-medium text-sm uppercase tracking-wider">
                  Færdig græsplæne
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-[#1A3C34] mt-2 mb-6" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Rullegræs
                </h2>
              </div>
              
              {/* Image - mobile only, appears after heading */}
              <div className="lg:hidden aspect-[4/3] overflow-hidden rounded-lg mb-8">
                {renderServiceImage(2, "Rullegræs")}
              </div>
              
              {/* Description, list, CTA */}
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Vi leverer og udlægger rullegræs med omhyggelig jordforberedelse og en ensartet etablering.
                Arbejdet tilpasses forholdene på stedet, så græsset får gode vækstbetingelser.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Jordforberedelse tilpasset eksisterende forhold</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Omhyggelig udlægning og samling af rullegræs</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Jævn overflade og tilpasning langs kanter og belægning</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Vejledning i pleje og vedligehold efter etablering</span>
                </li>
              </ul>
              <Link to="/kontakt" data-testid="service-cta-rullegraes" data-cta="primary" data-cta-variant="green" className="inline-flex items-center gap-2 bg-[#1A3C34] text-white rounded-full px-8 py-4 font-medium">
                Kontakt os <ArrowRight size={18} />
              </Link>
            </div>
            {/* Image - desktop only */}
            <div className="hidden lg:block aspect-[4/3] overflow-hidden rounded-lg">
              {renderServiceImage(2, "Rullegræs")}
            </div>
          </div>
        </div>
      </section>

      {/* Service 4 - Hegn */}
      <section id="hegn" data-testid="service-section-hegn" className="py-20 md:py-28 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="lg:order-2 flex flex-col">
              {/* Heading - always first */}
              <div>
                <span className="text-[#1A3C34] font-medium text-sm uppercase tracking-wider">
                  Solide løsninger
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-[#1A3C34] mt-2 mb-6" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Hegn
                </h2>
              </div>
              
              {/* Image - mobile only, appears after heading */}
              <div className="lg:hidden aspect-[4/3] overflow-hidden rounded-lg mb-8">
                {renderServiceImage(3, "Hegn")}
              </div>
              
              {/* Description, list, CTA */}
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Vi opsætter hegn i træ som en del af indretningen af udearealer.
                Opgaverne omfatter blandt andet raftehegn samt andre træhegn, udført med solide stolper og holdbare fundamenter.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Opsætning af raftehegn og andre træhegn</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Solide stolper og fundamenter tilpasset forholdene</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Tilpasning til terræn og eksisterende omgivelser</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Vejledning i udformning og materialevalg</span>
                </li>
              </ul>
              <Link to="/kontakt" data-testid="service-cta-hegn" data-cta="primary" data-cta-variant="green" className="inline-flex items-center gap-2 bg-[#1A3C34] text-white rounded-full px-8 py-4 font-medium">
                Kontakt os <ArrowRight size={18} />
              </Link>
            </div>
            {/* Image - desktop only */}
            <div className="lg:order-1 hidden lg:block aspect-[4/3] overflow-hidden rounded-lg">
              {renderServiceImage(3, "Hegn")}
            </div>
          </div>
        </div>
      </section>

      {/* Service 5 - Træfældning og beskæring */}
      <section id="traefaeldning" data-testid="service-section-traefaeldning" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="flex flex-col">
              {/* Heading - always first */}
              <div>
                <span className="text-[#1A3C34] font-medium text-sm uppercase tracking-wider">
                  Professionel træpleje
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-[#1A3C34] mt-2 mb-6" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Træfældning og beskæring
                </h2>
              </div>
              
              {/* Image - mobile only, appears after heading */}
              <div className="lg:hidden aspect-[4/3] overflow-hidden rounded-lg mb-8">
                {renderServiceImage(4, "Træfældning og beskæring")}
              </div>
              
              {/* Description, list, CTA */}
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Vi udfører træfældning og beskæring som selvstændige opgaver eller som del af større anlægsarbejder.
                Arbejdet vurderes og planlægges ud fra de konkrete forhold på stedet og udføres med kontrollerede arbejdsgange.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Træfældning efter vurdering af adgang og omgivelser</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Beskæring tilpasset træets vækst og placering</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Kontrolleret udførelse med hensyn til nærliggende forhold</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Oprydning og bortkørsel af grenaffald</span>
                </li>
              </ul>
              <Link to="/kontakt" data-testid="service-cta-traefaeldning" data-cta="primary" data-cta-variant="green" className="inline-flex items-center gap-2 bg-[#1A3C34] text-white rounded-full px-8 py-4 font-medium">
                Kontakt os <ArrowRight size={18} />
              </Link>
            </div>
            {/* Image - desktop only */}
            <div className="hidden lg:block aspect-[4/3] overflow-hidden rounded-lg">
              {renderServiceImage(4, "Træfældning og beskæring")}
            </div>
          </div>
        </div>
      </section>

      {/* Service 6 - Rodfræsning */}
      <section id="rodfraesning" data-testid="service-section-rodfraesning" className="py-20 md:py-28 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="lg:order-2 flex flex-col">
              {/* Heading - always first */}
              <div>
                <span className="text-[#1A3C34] font-medium text-sm uppercase tracking-wider">
                  Fjernelse af træstubbe
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-[#1A3C34] mt-2 mb-6" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Rodfræsning
                </h2>
              </div>
              
              {/* Image - mobile only, appears after heading */}
              <div className="lg:hidden aspect-[4/3] overflow-hidden rounded-lg mb-8">
                {renderServiceImage(5, "Rodfræsning")}
              </div>
              
              {/* Description, list, CTA */}
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Vi udfører rodfræsning af stubbe efter fældning eller på eksisterende træstubbe.
                Arbejdet tilpasses forholdene på stedet og udføres med maskiner, der matcher adgang og omgivelser.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Fræsning af stubbe i forskellige størrelser</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Tilpasning til adgangsforhold og omgivelser</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Stub fjernet til under terrænniveau</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Oprydning efter udført arbejde</span>
                </li>
              </ul>
              <Link to="/kontakt" data-testid="service-cta-rodfraesning" data-cta="primary" data-cta-variant="green" className="inline-flex items-center gap-2 bg-[#1A3C34] text-white rounded-full px-8 py-4 font-medium">
                Kontakt os <ArrowRight size={18} />
              </Link>
            </div>
            {/* Image - desktop only */}
            <div className="lg:order-1 hidden lg:block aspect-[4/3] overflow-hidden rounded-lg">
              {renderServiceImage(5, "Rodfræsning")}
            </div>
          </div>
        </div>
      </section>

      {/* Service 7 - Anlægsopgaver */}
      <section id="anlaeg" data-testid="service-section-anlaeg" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="flex flex-col">
              {/* Heading - always first */}
              <div>
                <span className="text-[#1A3C34] font-medium text-sm uppercase tracking-wider">
                  Renovering og etablering af udearealer
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-[#1A3C34] mt-2 mb-6" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Anlægsopgaver
                </h2>
              </div>
              
              {/* Image - mobile only, appears after heading */}
              <div className="lg:hidden aspect-[4/3] overflow-hidden rounded-lg mb-8">
                {renderServiceImage(6, "Anlægsopgaver")}
              </div>
              
              {/* Description, list, CTA */}
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Vi udfører anlægsopgaver i forbindelse med nye anlæg og renovering af eksisterende udearealer.
                Opgaverne tilpasses de eksisterende forhold og indgår ofte som en del af større helheder.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Renovering af eksisterende arealer</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Kantløsninger og niveauskift</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Opretning og reparation af belægning</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-[#1A3C34] mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-600">Asfaltopgaver</span>
                </li>
              </ul>
              <Link to="/kontakt" data-testid="service-cta-anlaeg" data-cta="primary" data-cta-variant="green" className="inline-flex items-center gap-2 bg-[#1A3C34] text-white rounded-full px-8 py-4 font-medium">
                Kontakt os <ArrowRight size={18} />
              </Link>
            </div>
            {/* Image - desktop only */}
            <div className="hidden lg:block aspect-[4/3] overflow-hidden rounded-lg">
              {renderServiceImage(6, "Anlægsopgaver")}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A3C34] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6" style={{ fontFamily: "Manrope, sans-serif" }}>
            Har du en opgave?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Kontakt os for en uforpligtende snak. Vi svarer inden for 24 timer.
          </p>
          <Link to="/kontakt" data-testid="services-cta-contact" data-cta="primary" data-cta-variant="white" className="inline-flex items-center gap-2 bg-white text-[#1A3C34] rounded-full px-10 py-4 text-lg font-medium">
            Få et tilbud <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
